<template>
  <div class="eom-widget">
    <div v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate size="28" color="blue darken-1" />
    </div>
    <div v-else-if="fetchError" class="text-center py-4">
      <v-icon color="grey lighten-1" size="32">mdi-alert-circle-outline</v-icon>
      <p class="text-caption grey--text mt-2">Unable to load data</p>
      <v-btn
        v-if="monthOffset !== 0"
        text
        x-small
        color="blue darken-1"
        @click="setMonthOffset(0)"
      >
        Show current month
      </v-btn>
    </div>
    <div v-else class="kpi-card-body">
      <div class="d-flex align-center justify-space-between mb-2">
        <div>
          <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2">
            EOM Readiness
          </div>
          <div class="d-flex align-center month-nav">
            <v-btn
              icon
              x-small
              class="month-nav-btn"
              :disabled="monthOffset === -1"
              aria-label="Show previous month"
              @click="setMonthOffset(-1)"
            >
              <v-icon small color="grey">{{ icons.mdiChevronLeft }}</v-icon>
            </v-btn>
            <div class="text-caption grey--text month-label text-center">
              {{ monthLabel }}
            </div>
            <v-btn
              icon
              x-small
              class="month-nav-btn"
              :disabled="monthOffset === 0"
              aria-label="Show current month"
              @click="setMonthOffset(0)"
            >
              <v-icon small color="grey">{{ icons.mdiChevronRight }}</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="text-right">
          <div
            class="kpi-count font-weight-bold"
            :class="toneText(completedCount === totalCount ? 'green' : 'blue')"
          >
            {{ completedCount }}<span class="kpi-denom">/{{ totalCount }}</span>
          </div>
          <div class="text-caption grey--text">Complete</div>
        </div>
      </div>

      <v-progress-linear
        :value="totalCount > 0 ? (completedCount / totalCount) * 100 : 0"
        :color="completedCount === totalCount ? 'green darken-1' : 'blue darken-1'"
        height="8" rounded
        :background-color="isDark ? 'grey darken-3' : 'grey lighten-3'"
        class="mb-4"
      />

      <div
        v-for="step in steps"
        :key="step.id"
        class="step-row d-flex align-start mb-2"
      >
        <div class="step-icon-wrap mr-2 mt-1">
          <v-icon v-if="step.status === 'complete'" small :color="toneColor('green')">
            {{ icons.mdiCheckCircleOutline }}
          </v-icon>
          <v-icon v-else-if="step.status === 'pending'" small :color="toneColor('orange')">
            {{ icons.mdiClockOutline }}
          </v-icon>
          <v-icon v-else small color="grey lighten-1">
            {{ icons.mdiCircleOutline }}
          </v-icon>
        </div>

        <div class="flex-grow-1" style="min-width: 0;">
          <div class="text-caption font-weight-medium" :class="stepTextClass(step)">
            {{ step.label }}
          </div>
          <div class="text-caption grey--text" style="font-size: 10px;">
            {{ step.detail }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mdiCheckCircleOutline,
  mdiClockOutline,
  mdiCircleOutline,
  mdiChevronLeft,
  mdiChevronRight,
} from '@mdi/js';
import { clients } from '../../../util/clients';
import WidgetThemeMixin from '../WidgetTheme.mixin';

const { backendRest } = clients.direct;

export default {
  name: 'EomReadinessWidget',
  mixins: [WidgetThemeMixin],
  data: () => ({
    loading: true,
    fetchError: false,
    completedCount: 0,
    totalCount: 6,
    steps: [],
    monthLabel: '',
    // 0 = current calendar month, -1 = previous month.
    monthOffset: 0,
    icons: {
      mdiCheckCircleOutline,
      mdiClockOutline,
      mdiCircleOutline,
      mdiChevronLeft,
      mdiChevronRight,
    },
  }),
  async created() {
    await this.fetchData();
  },
  methods: {
    stepTextClass(step) {
      if (step.status === 'complete') return this.toneText('green');
      if (step.status === 'pending') return this.toneText('orange');
      return 'grey--text';
    },
    setMonthOffset(offset) {
      // Only current (0) and previous (-1) are offered.
      const clamped = Math.max(-1, Math.min(0, offset));
      if (clamped === this.monthOffset) return;
      this.monthOffset = clamped;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        // Resolve the month to show from monthOffset. Set the day to 1 first so
        // subtracting a month can't skip one on a 31-day date, and let Date
        // handle the January -> December (prior year) rollover for offset -1.
        const base = new Date();
        base.setDate(1);
        base.setMonth(base.getMonth() + this.monthOffset);
        const month = base.getMonth() + 1;
        const year = base.getFullYear();
        this.monthLabel = base.toLocaleDateString('en-US', {
          month: 'long', year: 'numeric',
        });
        const response = await backendRest.get('/dashboard/eom-status', {
          params: { month, year, fiscalYear: year },
        });
        const data = response.data || {};
        this.completedCount = data.completedCount || 0;
        this.totalCount = data.totalCount || 6;
        this.steps = data.steps || [];
        this.fetchError = false;
      } catch (e) {
        console.error('EomReadinessWidget fetch failed', e);
        this.fetchError = true;
        this.completedCount = 0;
        this.totalCount = 6;
        this.steps = [];
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      await this.fetchData();
    },
  },
};
</script>

<style scoped>
.eom-widget { position: relative; }
.kpi-card-body { padding: 4px 0; }
.kpi-count {
  font-size: 2rem; font-weight: 800; line-height: 1; letter-spacing: -1px;
}
.kpi-denom {
  font-size: 1rem; font-weight: 400;
  color: var(--widget-muted-text, #9e9e9e); letter-spacing: 0;
}
.step-row {
  padding: 4px 0;
  border-bottom: 1px solid var(--widget-hairline, #f5f5f5);
}
.step-row:last-child { border-bottom: none; }
.step-icon-wrap { flex-shrink: 0; width: 20px; }
.month-nav { margin-top: 1px; margin-left: -4px; }
.month-nav-btn { width: 18px; height: 18px; }
.month-label { min-width: 96px; }
</style>
